import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/arina';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '桥本有菜官方资料站',
    },
    publisher: {
      '@type': 'Organization',
      name: '桥本有菜官方资料站',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hashimoto-arina.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />
        <main className="pt-24 pb-20">
          <article className="w-full max-w-4xl mx-auto px-6">
            <Breadcrumb items={getArticleBreadcrumb(article)} />

            <div className="mt-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                  {category?.name}
                </span>
                <span className="text-sm text-foreground-400">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose max-w-none text-foreground-700 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-secondary-50 text-secondary-700 text-xs font-medium rounded-full border border-secondary-200/50 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mt-1">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}